import { NewOrganizationPayload } from "@API/Organizations/types";
import { sortFunction } from "@Components/Table";
import {
  combineReducers,
  createReducer,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  createOrganizationFail,
  createOrganizationRequest,
  createOrganizationSuccess,
  organizationsListFail,
  organizationsListRequest,
  organizationsListSuccess,
  resetCreateOrganizationModal,
  setModalCreateOrganization,
  setModalCreateOrganizationType,
  updateOrganizationFail,
  updateOrganizationRequest,
  updateOrganizationSuccess,
  updateSelectedOrganizationId,
} from "./actions";
import {
  CreateOrganizationModal,
  IOrganization,
  OrganizationId,
  OrganizationModalType,
} from "./types";

interface IOrganizationsListState {
  organizationsList: IOrganization[];
  createOrganizationModal: CreateOrganizationModal;
  selectedOrganizationId: OrganizationId | undefined;
  noOrganization: boolean;
  updateStatus: {
    isLoading: boolean;
    success: boolean;
    fail: boolean;
  };
  isLoading: boolean;
  success: boolean;
  fail: boolean;
}

const ORGANIZATION_LIST_INITIAL_STATE: IOrganizationsListState = {
  organizationsList: [],
  createOrganizationModal: {
    organization: undefined,
    type: undefined,
    isLoading: false,
    success: false,
    fail: false,
  },
  selectedOrganizationId: undefined,
  noOrganization: false,
  updateStatus: {
    isLoading: false,
    success: false,
    fail: false,
  },
  isLoading: false,
  success: false,
  fail: false,
};

const organizationsListReducer = createReducer(
  ORGANIZATION_LIST_INITIAL_STATE,
  {
    [organizationsListRequest.type]: (state) => ({
      ...state,
      isLoading: true,
      success: false,
      fail: false,
    }),
    [organizationsListSuccess.type]: (
      state,
      { payload }: PayloadAction<IOrganization[]>
    ) => ({
      ...state,
      organizationsList: payload,
      noOrganization: !payload.length,
      isLoading: false,
      success: true,
      fail: false,
    }),
    [organizationsListFail.type]: (state) => ({
      ...state,
      isLoading: false,
      success: false,
      fail: true,
    }),

    [updateOrganizationRequest.type]: (state) => ({
      ...state,
      createOrganizationModal: {
        ...state.createOrganizationModal,
        isLoading: true,
        success: false,
        fail: false,
      },
      updateStatus: {
        isLoading: true,
        success: false,
        fail: false,
      },
    }),
    [updateOrganizationSuccess.type]: (
      state,
      { payload }: PayloadAction<IOrganization>
    ) => ({
      ...state,
      organizationsList: state.organizationsList.map((item) => {
        if (item.orgId !== payload.orgId) return item;
        else
          return {
            ...item,
            createOrganizationModal: {
              ...state.createOrganizationModal,
              isLoading: false,
              success: true,
              fail: false,
            },
            organization: {
              ...item,
              ...payload,
            },
          };
      }),
      updateStatus: {
        isLoading: false,
        success: true,
        fail: false,
      },
    }),
    [updateOrganizationFail.type]: (state) => ({
      ...state,
      createOrganizationModal: {
        ...state.createOrganizationModal,
        isLoading: false,
        success: false,
        fail: true,
      },
      updateStatus: {
        isLoading: false,
        success: false,
        fail: true,
      },
    }),

    [updateSelectedOrganizationId.type]: (
      state,
      { payload }: PayloadAction<OrganizationId>
    ) => ({
      ...state,
      selectedOrganizationId: payload,
    }),

    [createOrganizationRequest.type]: (state) => ({
      ...state,
      createOrganizationModal: {
        ...state.createOrganizationModal,
        isLoading: true,
        success: false,
        fail: false,
      },
    }),
    [createOrganizationSuccess.type]: (
      state,
      { payload }: PayloadAction<IOrganization>
    ) => ({
      ...state,
      organizationsList: [...state.organizationsList, payload].sort(
        sortFunction((organization: IOrganization) =>
          organization.orgName.toLowerCase()
        )
      ),
      createOrganizationModal: {
        ...state.createOrganizationModal,
        isLoading: false,
        success: true,
        fail: false,
      },
    }),
    [createOrganizationFail.type]: (state) => ({
      ...state,
      createOrganizationModal: {
        ...state.createOrganizationModal,
        isLoading: false,
        success: false,
        fail: true,
      },
    }),

    [setModalCreateOrganizationType.type]: (
      state,
      { payload }: PayloadAction<OrganizationModalType>
    ) => {
      return {
        ...state,
        createOrganizationModal: {
          ...state.createOrganizationModal,
          type: payload,
        },
      };
    },
    [setModalCreateOrganization.type]: (state) => {
      return {
        ...state,
        createOrganizationModal: {
          ...state.createOrganizationModal,
          organization: {} as NewOrganizationPayload,
        },
      };
    },
    [resetCreateOrganizationModal.type]: (state) => ({
      ...state,
      createOrganizationModal: {
        organization: undefined,
        type: undefined,
        isLoading: false,
        success: false,
        fail: false,
      },
    }),
  }
);

export interface IGeneralState {
  readonly system: IOrganizationsListState;
}

export default combineReducers<IGeneralState>({
  system: organizationsListReducer,
});
